import React from "react";

export type DSCardWrapperVariant =
  | "default"
  | "modal"
  | "danger"
  | "warning"
  | "success"
  | "info"
  | "ghost";

export interface DSCardWrapperProps {
  children?: React.ReactNode;
  interactive?: boolean;
  variant?: DSCardWrapperVariant;
  width?: string;
  height?: string;
  padding?: string;
  onClick?: () => void;
  /** Accessible label for screen readers when interactive */
  "aria-label"?: string;
  dataTestId: string;
  ref?: React.Ref<HTMLDivElement>;
}

export const DSCardWrapper = ({
  children,
  interactive = false,
  variant = "default",
  width = "w-full",
  height,
  padding = "p-md",
  onClick,
  "aria-label": ariaLabel,
  dataTestId,
  ref,
}: DSCardWrapperProps) => {
  // Base styles
  const baseStyles = `rounded-large transition-all ${padding}`;

  // Interactive styles (cursor and hover)
  const interactiveStyles = interactive
    ? "cursor-pointer hover:bg-secondary hover:shadow-[0px_0px_15px_0px_var(--shadow-primary)] focus:outline-none"
    : "";

  // Variant styles
  const variantStyles = {
    default:
      "bg-secondary shadow-[0px_0px_10px_0px_var(--shadow-secondary)]",
    modal:
      "bg-primary shadow-[0px_0px_10px_0px_var(--shadow-secondary)] flex flex-col overflow-hidden",
    danger:
      "bg-danger-subtle border border-danger-subtle shadow-[0px_0px_10px_0px_var(--shadow-secondary)]",
    warning:
      "bg-warning-subtle border border-warning-subtle shadow-[0px_0px_10px_0px_var(--shadow-secondary)]",
    success:
      "bg-success-subtle border border-success-subtle shadow-[0px_0px_10px_0px_var(--shadow-secondary)]",
    info: "bg-brand-subtle border border-brand-subtle shadow-[0px_0px_10px_0px_var(--shadow-secondary)]",
    ghost: "bg-primary border border-secondary border-dashed",
  };

  // Handle keyboard interaction for interactive cards
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || !onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={interactive ? ariaLabel : undefined}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      className={`${baseStyles} ${
        variantStyles[variant]
      } ${interactiveStyles} ${width} ${height || ""}`}
    >
      {children}
    </div>
  );
};

DSCardWrapper.displayName = "DSCardWrapper";
